/**
 * Home as project cards (Wave Experience A, item 1).
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * Schema truth: HomeResponse {total, items[HomeProjectCard]} (frontend/src/api/schema.d.ts).
 * Every number is the server's, counted over the caller's own visible rows; the client
 * never sums a register to draw a card.
 */

import { apiJson, isRecord, listBody, num, str } from "./client";

export interface HomeNextActivity {
  readonly id: string;
  readonly humanId: string;
  readonly plannedDate: string;
  readonly status: string;
}

export interface HomeQuality {
  readonly findingsOpen: number;
  readonly findingsClosed: number;
  readonly punchOpen: number;
  readonly punchClosed: number;
  readonly inspectionsOpen: number;
  readonly inspectionsClosed: number;
  readonly rfisOpen: number;
}

export interface HomeProjectCard {
  readonly projectId: string;
  readonly humanId: string;
  readonly name: string;
  readonly status: string;
  /** Open tasks under this project that close only through their act: waiting on me. */
  readonly actionableTaskCount: number;
  /** Informational tasks and unread bells that announce no task. */
  readonly fyiCount: number;
  readonly nextActivity: HomeNextActivity | null;
  readonly quality: HomeQuality;
}

export interface HomeCards {
  readonly total: number;
  readonly items: readonly HomeProjectCard[];
}

function parseQuality(raw: unknown): HomeQuality {
  if (!isRecord(raw)) {
    throw new Error("Expected a quality record");
  }
  return {
    findingsOpen: num(raw.findings_open),
    findingsClosed: num(raw.findings_closed),
    punchOpen: num(raw.punch_open),
    punchClosed: num(raw.punch_closed),
    inspectionsOpen: num(raw.inspections_open),
    inspectionsClosed: num(raw.inspections_closed),
    rfisOpen: num(raw.rfis_open),
  };
}

function parseCard(raw: Record<string, unknown>): HomeProjectCard {
  const next = raw.next_activity;
  return {
    projectId: str(raw.project_id),
    humanId: str(raw.human_id),
    name: str(raw.name),
    status: str(raw.status),
    actionableTaskCount: num(raw.actionable_task_count),
    fyiCount: num(raw.fyi_count),
    nextActivity: isRecord(next)
      ? {
          id: str(next.id),
          humanId: str(next.human_id),
          plannedDate: str(next.planned_date),
          status: str(next.status),
        }
      : null,
    quality: parseQuality(raw.quality),
  };
}

export async function fetchHomeCards(token: string, signal?: AbortSignal): Promise<HomeCards> {
  return apiJson(
    token,
    "/me/home",
    (raw) => {
      const body = listBody(raw);
      return { total: body.total, items: body.items.map(parseCard) };
    },
    { signal },
  );
}
